import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { FixtureForm } from "./components";
import PageHeader from "../../components/common/PageHeader";
import ErrorDisplay from "../../components/common/ErrorDisplay";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import fixtureService from "../../services/fixtureService";
import { FixtureType, FixtureFormData } from "../../types/fixture";

interface FixtureFormPageProps {
  mode?: "create" | "edit";
}

const FixtureFormPage: React.FC<FixtureFormPageProps> = ({
  mode = "create",
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { error, setError, clearError } = useErrorHandling();

  const [fixture, setFixture] = useState<FixtureType | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isEditMode = mode === "edit" || !!id;
  const pageTitle = isEditMode
    ? t("fixture.editTitle")
    : t("fixture.createTitle");

  useEffect(() => {
    if (isEditMode && id) {
      loadFixtureDetail(id);
    }
  }, [isEditMode, id]);

  const loadFixtureDetail = async (fixtureId: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await fixtureService.getFixtureById(fixtureId);

      if (response.success && response.data) {
        setFixture(response.data);
      } else {
        message.error(response.errorMessage || t("fixture.saveFailed"));
      }
    } catch (err) {
      message.error(error.message || t("fixture.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData: FixtureFormData) => {
    try {
      setSubmitting(true);
      clearError();

      let response;
      if (isEditMode && id) {
        response = await fixtureService.updateFixture(id, formData);
      } else {
        response = await fixtureService.createFixture(formData);
      }

      if (response.success) {
        message.success(
          isEditMode ? t("fixture.updateSuccess") : t("fixture.createSuccess")
        );
        navigate("/fixture/list");
      } else {
        message.error(response.errorMessage || t("fixture.saveFailed"));
      }
    } catch (err) {
      message.error(error.message || t("fixture.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/fixture/list");
  };

  if (error) {
    return (
      <div>
        <PageHeader title={pageTitle} backPath="/fixture/list" />
        <ErrorDisplay
          error={error}
          onRetry={() => id && loadFixtureDetail(id)}
        />
      </div>
    );
  }

  if (isEditMode && loading) {
    return (
      <div>
        <PageHeader title={pageTitle} backPath="/fixture/list" />
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={pageTitle} backPath="/fixture/list" />

      <FixtureForm
        fixture={fixture || undefined}
        mode={isEditMode ? "edit" : "create"}
        onSave={handleSave}
        onCancel={handleCancel}
        loading={submitting}
      />
    </div>
  );
};

export default FixtureFormPage;
